import { useMemo } from 'react';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';

// Single gate for the displayed price unit on the whole detail funnel. Prices, unit labels and tax
// breakdowns all have to read this one hook, or they drift apart from each other.
//
// Only a PER_PERSON price can become a group total. A TOTAL or PER_ROOM_NIGHT price already covers
// every traveller — which is why the server's calculateTotalPrice leaves those types untouched — so
// nothing on the page may scale them by pax or relabel them as totals, and the toggle itself has
// nothing to offer there.
//
export default function usePriceDisplay() {
  const { priceType } = useInitialParams();
  const { totalGuestsWithoutInfants } = useUrlSearchParams();
  const { priceTogglerEnabled, familyTotalPrice, showTotalPrice: prefersTotalPrice } = usePriceMode();

  const isPerPerson = priceType === 'PER_PERSON';
  // The toggle affordance, not the unit: the family test shows totals with no switch to offer.
  const canShowTotalPrice = priceTogglerEnabled && isPerPerson;
  const isFamilyTotalPrice = familyTotalPrice && isPerPerson;
  const showTotalPrice = (canShowTotalPrice && prefersTotalPrice) || isFamilyTotalPrice;

  return useMemo(
    () => ({
      canShowTotalPrice,
      familyTotalPrice: isFamilyTotalPrice,
      showTotalPrice,
      // Approximate. Only for figures with no server total of their own (strike-throughs, increments).
      toDisplayPrice: (perPersonPrice: number) =>
        showTotalPrice ? perPersonPrice * totalGuestsWithoutInfants : perPersonPrice,
    }),
    [canShowTotalPrice, isFamilyTotalPrice, showTotalPrice, totalGuestsWithoutInfants]
  );
}
